'use client'

// Curated, result-matched product block for the calculator tools — the soil-test
// pattern made reusable. Renders a titled list of ProductPickCard (each with a
// hand-written "why this product" reason) plus the FTC affiliate disclosure.
//
// Fed by a tool's get<Tool>Picks(result) -> ProductRecommendationLink[]. Returns
// null when there are no curated picks, so the caller can fall back to the generic
// (category/keyword) ProductRecommendations and coverage never regresses.

import { Package } from 'lucide-react'
import type { ProductRecommendationLink } from '@/lib/product-picks/types'
import { ProductPickCard } from './product-pick-card'

interface CuratedProductPicksProps {
  picks: ProductRecommendationLink[]
  title: string
  description?: string
  /** Analytics article_slug, e.g. 'tool:herbicide-timing'. */
  trackingSlug: string
  /** Analytics source_context for clicks. Defaults to 'curated-picks'. */
  sourceContext?: string
}

export function CuratedProductPicks({
  picks,
  title,
  description,
  trackingSlug,
  sourceContext = 'curated-picks',
}: CuratedProductPicksProps) {
  if (picks.length === 0) return null

  return (
    <section aria-label={title} className="rounded-2xl border border-gray-200 bg-white p-4 md:p-5">
      <div className="mb-3">
        <h3 className="inline-flex items-center gap-2 text-sm font-bold tracking-tight text-gray-900">
          <Package className="h-4 w-4 text-green-600" aria-hidden />
          {title}
        </h3>
        {description && <p className="mt-1 text-xs text-gray-500">{description}</p>}
      </div>
      <ul className="list-none p-0 m-0 space-y-2.5" aria-label="Recommended products">
        {picks.map((product, index) => (
          <li key={product.asin ?? product.name} className="list-none">
            <ProductPickCard
              product={product}
              pickIndex={index}
              variant={index === 0 ? 'primary' : 'alternative'}
              trackingSlug={trackingSlug}
              sourceContext={sourceContext}
            />
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-gray-600 text-center">
        As an Amazon Associate, we earn from qualifying purchases.
      </p>
    </section>
  )
}
